// src/arrays/arrayToIdKeyedObject.ts
var arrayToIdKeyedObject = (items) => items?.filter((item) => item?.id).reduce(
  (acc, item) => ({ ...acc, [item.id]: item }),
  {}
) ?? {};

// src/billingFrequency/toBillingFrequency.ts
var typeDescriptions = {
  ONE_TIME: {
    label: "One time",
    name: "one time",
    pluralName: "one time"
  },
  ON_DEMAND: {
    label: "On demand",
    name: "on demand",
    pluralName: "on demand"
  },
  MONTHLY: {
    label: "Monthly",
    name: "month",
    pluralName: "months"
  },
  QUARTERLY: {
    label: "Quarterly",
    name: "quarter",
    pluralName: "quarters"
  },
  HALF_YEARLY: {
    label: "Semi-annually",
    name: "semi-annual",
    pluralName: "semi-annual"
  },
  YEARLY: {
    label: "Annually",
    name: "year",
    pluralName: "years"
  }
};
var toBillingFrequencyLabel = (value, fallback = " - ") => value ? typeDescriptions[value].label : fallback;
var toBillingFrequencyName = (value, fallback) => typeDescriptions[value]?.name ?? fallback;
var toBillingFrequencyPluralName = (value, fallback) => typeDescriptions[value]?.pluralName ?? fallback;

// src/billingType/toBillingType.ts
import { match } from "ts-pattern";
var toBillingType = (billingType) => {
  return match(billingType).with("IN_ARREARS", () => "In arrears").with("IN_ADVANCE", () => "In advance").exhaustive();
};

// src/blobs/downloadBlob.ts
var downloadBlob = (fileName) => (blob) => {
  const url = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement("a"), {
    download: fileName,
    href: url
  });
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// src/blobs/convertBlobToBase64.ts
var convertBlobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    if (!event.target) {
      return reject(`event.target is null`);
    }
    const dataUrl = event.target.result;
    if (typeof dataUrl !== "string") {
      return reject(`dataUrl is not string`);
    }
    return resolve(dataUrl);
  };
  reader.readAsDataURL(blob);
});

// src/config/config.ts
var getConfig = (source, envVars) => {
  const result = Object.entries(envVars).reduce(
    ({ config, missingEnvVars }, item) => {
      const configKey = item[0];
      const envVarKey = item[1];
      const envVar = source[envVarKey];
      if (envVar) {
        return {
          config: {
            ...config,
            [configKey]: envVar
          },
          missingEnvVars
        };
      }
      return {
        config,
        missingEnvVars: [...missingEnvVars, envVarKey]
      };
    },
    { config: {}, missingEnvVars: [] }
  );
  if (result.missingEnvVars.length) {
    throw new Error(
      `Missing environment variables: ${result.missingEnvVars.join(", ")}`
    );
  }
  return result.config;
};

// src/config/sentry.ts
var reactSentryEnvVars = {
  dsn: "VITE_SENTRY_DSN",
  environment: "VITE_SENTRY_ENVIRONMENT"
};
var nodeSentryEnvVars = {
  dsn: "SENTRY_DSN",
  environment: "SENTRY_ENVIRONMENT"
};
var getReactSentryConfig = (source) => getConfig(source, reactSentryEnvVars);
var getNodeSentryConfig = (source) => getConfig(source, nodeSentryEnvVars);

// src/config/auth.ts
var authEnvVars = {
  client_id: "VITE_CLIENT_ID",
  redirect_uri: "VITE_REDIRECT_URI",
  authorization_endpoint: "VITE_AUTHORIZATION_ENDPOINT",
  token_endpoint: "VITE_TOKEN_ENDPOINT",
  requested_scopes: "VITE_REQUESTED_SCOPES",
  token_revoke_endpoint: "VITE_TOKEN_REVOKE_ENDPOINT",
  auth_url: "VITE_AUTH_URL"
};
var getAuthConfig = (source) => getConfig(source, authEnvVars);

// src/config/userInfo.ts
var userInfoEnvVars = {
  userInfoUrl: "USER_INFO_URL"
};
var getUserInfoConfig = (source) => getConfig(source, userInfoEnvVars);

// src/config/sdk.ts
var sdkEnvVars = {
  sdkBasePath: "BASE_PATH"
};
var getSdkConfig = (source) => getConfig(source, sdkEnvVars);

// src/config/authWeb.ts
var authWebEnvVars = {
  dashboardUrl: "DASHBOARD_URL"
};
var getAuthWebConfig = (source) => getConfig(source, authWebEnvVars);

// src/config/getPortalDomain.ts
var getPortalDomain = (environment) => {
  switch (environment) {
    case "local":
      return "http://localhost:3002";
    case "dev":
      return "https://portal.seqhq.io";
    case "sandbox":
      return "https://portal.sandbox.sequencehq.com";
    case "production":
      return "https://portal.sequencehq.com";
    case "demo":
      return "https://portal.sequencehq.com";
    default:
      return void 0;
  }
};

// src/developerLog.ts
var developerLog = (...args) => {
  if (localStorage.getItem("enable-sequence-logs") === "true") {
    return console.info(...args);
  }
  return;
};

// src/formatters/formatState.ts
import { states } from "@sequencehq/core-models";
var toStateLabel = (state) => {
  return states[state];
};

// src/formatters/formatAddress.ts
import { countries, states as states2 } from "@sequencehq/core-models";
var formatAddress = (address) => {
  return [
    address.line1,
    address.line2,
    address.town,
    ...address.state ? [states2[address.state]] : [],
    address.postcode,
    countries[address.country]
  ].filter((str) => Boolean(str));
};

// src/hooks/useForm/useForm.ts
import { useCallback, useEffect, useMemo, useState } from "react";

// src/hooks/useForm/evaluateValidators.ts
import { dequal } from "dequal";
import get from "lodash/fp/get";
import { match as match2 } from "ts-pattern";
var OPERATORS = {
  eq: (lhs, rhs) => dequal(lhs, rhs),
  neq: (lhs, rhs) => !dequal(lhs, rhs),
  and: (lhs, rhs) => !!lhs && !!rhs,
  or: (lhs, rhs) => !!lhs || !!rhs
};
var resolveValue = (prevState) => (config) => {
  return match2(config).with(
    { type: "resolveState" },
    (matchedConfig) => get(matchedConfig.dataLocation)(prevState)
  ).with({ type: "static" }, (matchedConfig) => matchedConfig.value).with(
    {
      type: "expression"
    },
    (matchedConfig) => evaluateExpressionConfig(prevState)(matchedConfig.value)
  ).exhaustive();
};
var evaluateExpressionConfig = (prevState) => (config) => {
  return (Array.isArray(config) ? config : [config]).every(
    ({ lhsValue, rhsValue, operator }) => {
      const resolvedLhs = resolveValue(prevState)(lhsValue);
      const resolvedRhs = resolveValue(prevState)(rhsValue);
      return OPERATORS[operator](resolvedLhs, resolvedRhs);
    }
  );
};
var evaluateValidators = (config) => (prevState) => config.flatMap(
  (validatorConfig) => validatorConfig.validators.flatMap((fn) => {
    if (validatorConfig.ignoreIf) {
      const shouldIgnore = typeof validatorConfig.ignoreIf === "function" ? validatorConfig.ignoreIf(prevState) : evaluateExpressionConfig(prevState)(validatorConfig.ignoreIf);
      if (shouldIgnore) {
        return [];
      }
    }
    const resolvedValue = get(validatorConfig.dataLocation)(prevState);
    const resolvedAdditionalData = validatorConfig.additionalData ? Object.entries(validatorConfig.additionalData).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: get(value)(prevState)
      }),
      {}
    ) : prevState;
    const errors = fn(resolvedValue, resolvedAdditionalData);
    if (!errors) {
      return;
    }
    return Array.isArray(errors) ? errors : [
      {
        message: errors
      }
    ];
  }).filter(
    (validationError) => Boolean(validationError)
  ).map((validationError) => {
    return {
      fieldName: validatorConfig.fieldName,
      message: validationError.message,
      metadata: validationError.metadata,
      value: get(validatorConfig.dataLocation)(prevState)
    };
  })
);

// src/hooks/useForm/useForm.ts
import { dequal as dequal2 } from "dequal";
import get2 from "lodash/fp/get";
import set from "lodash/fp/set";
import deepmerge from "deepmerge";
var useForm = ({
  value,
  fieldConfiguration,
  showValidationErrors,
  onChange,
  onValidationStateChange,
  disabled = false
}) => {
  const [prevIsValid, setPrevIsValid] = useState(null);
  const [state, setState] = useState({
    formData: value,
    lastValue: value
  });
  const validationConfiguration = useMemo(() => {
    return fieldConfiguration.filter(
      (fieldConfig) => fieldConfig.validation && !disabled && !(fieldConfig.disabled?.({
        formData: state.formData,
        property: fieldConfig.property
      }) ?? false)
    ).flatMap((fieldConfig) => {
      return {
        fieldName: fieldConfig.property,
        dataLocation: fieldConfig.property,
        validators: fieldConfig.validation
      };
    });
  }, [fieldConfiguration, state]);
  const resetFormData = useCallback(() => {
    setState({
      formData: value,
      lastValue: value
    });
  }, [value]);
  const updateFormData = useCallback(
    (update) => {
      const newData = deepmerge(state.formData, update, {
        arrayMerge: (_, source) => source
      });
      setState((prevState) => ({
        ...prevState,
        formData: deepmerge(prevState.formData, update, {
          arrayMerge: (_, source) => source
        })
      }));
      onChange?.(newData);
      const validationResults2 = evaluateValidators(validationConfiguration)(
        newData
      );
      onValidationStateChange?.(!validationResults2.length, validationResults2);
    },
    [state, onChange, onValidationStateChange, validationConfiguration]
  );
  useEffect(() => {
    if (!dequal2(value, state.lastValue)) {
      setState({
        formData: value,
        lastValue: value
      });
    }
  }, [value, state, onValidationStateChange, validationConfiguration]);
  const validationResults = useMemo(() => {
    return evaluateValidators(validationConfiguration)(state.formData);
  }, [validationConfiguration, state]);
  const isValid = useMemo(() => {
    return !validationResults.length;
  }, [validationResults.length]);
  useEffect(() => {
    if (isValid !== prevIsValid || prevIsValid === null) {
      setPrevIsValid(isValid);
      onValidationStateChange?.(isValid, validationResults);
    }
  }, [isValid, onValidationStateChange, prevIsValid, validationResults]);
  const formFields = useMemo(() => {
    return fieldConfiguration.reduce((acc, fieldConfig) => {
      const propertyPath = (fieldConfig.property ?? "").split(".").filter(Boolean);
      return {
        ...acc,
        [fieldConfig.property]: {
          disabled: disabled || (fieldConfig.disabled?.({
            formData: state.formData,
            property: fieldConfig.property
          }) ?? false),
          value: get2(propertyPath)(state.formData),
          onChange: (newValue) => updateFormData(
            set(propertyPath, newValue)({})
          ),
          validationErrors: showValidationErrors ? validationResults.filter(
            (error) => error.fieldName === fieldConfig.property
          ) : [],
          options: fieldConfig.options ?? []
        }
      };
    }, {});
  }, [
    fieldConfiguration,
    updateFormData,
    state,
    validationResults,
    showValidationErrors
  ]);
  return {
    fields: formFields,
    queries: {
      formData: state.formData,
      isValid,
      isDirty: !dequal2(state.formData, value),
      validationResults
    },
    mutators: {
      resetFormData,
      updateFormData
    }
  };
};

// src/invoices/paymentStatus.ts
var statusToFormat = {
  UNPAID: {
    children: "Unpaid",
    sentiment: "neutral"
  },
  PARTIALLY_PAID: {
    children: "Partially paid",
    sentiment: "neutral"
  },
  PAID: {
    children: "Paid",
    sentiment: "success"
  },
  UNCOLLECTIBLE: {
    children: "Uncollectible",
    sentiment: "warning"
  }
};
var toInvoicePaymentStatusBadgeProps = ({
  status,
  size = "md"
}) => ({
  ...statusToFormat[status],
  size
});

// src/invoices/status.ts
var statusToFormat2 = {
  IN_PROGRESS: {
    children: "Upcoming",
    sentiment: "info"
  },
  DRAFT: {
    children: "Draft",
    sentiment: "neutral"
  },
  FINAL: {
    children: "Final",
    sentiment: "info"
  },
  VOIDED: {
    children: "Void",
    sentiment: "neutral"
  },
  SENT: {
    children: "Sent",
    sentiment: "success"
  }
};
var toInvoiceStatusBadgeProps = ({
  status,
  size = "md"
}) => ({
  ...statusToFormat2[status],
  size
});

// src/money/toMoney.ts
var displayWithNarrowSymbol = ["USD"];
var currencyDisplayOptions = (currency) => {
  let currencyDisplay;
  if (displayWithNarrowSymbol.includes(currency)) {
    currencyDisplay = "narrowSymbol";
  } else {
    currencyDisplay = "symbol";
  }
  return currencyDisplay;
};
var toIntlNumber = (currency, maximumFractionDigits = 2) => {
  const currencyDisplay = currencyDisplayOptions(currency);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currencyDisplay,
    currency,
    maximumFractionDigits
  });
};
var toMoney = ({ value, currency }, fallback = "", maximumFractionDigits = 2) => {
  if (!value || !currency) {
    return fallback;
  }
  return toIntlNumber(currency, maximumFractionDigits).format(parseFloat(value));
};

// src/parsers/links.ts
var isExternalLink = (link) => {
  const pattern = /^[^\/]/;
  return pattern.test(link);
};

// src/percentage/decimalFromPercentage.ts
import Big from "big.js";
function percentageFromDecimal(percentage, decimalPadding) {
  const value = Big(Big(percentage).times(100));
  if (decimalPadding) {
    return value.toFixed(decimalPadding).padEnd(value.toFixed(0).length + 1 + decimalPadding, "0");
  }
  return value.toFixed(10).replace(/\.?0+$/, "").toString();
}
function decimalFromPercentage(percentage) {
  return Big(Big(percentage).div(100)).toFixed(10).toString();
}

// src/prices/calculateDiscountedPrice.ts
import { toMoney as toMoney2 } from "@sequencehq/core-models";
import { match as match3 } from "ts-pattern";
var getPriceStructureAmount = ({
  structure: priceStructure
}) => {
  return match3(priceStructure).with(
    { pricingType: "FIXED" },
    { pricingType: "ONE_TIME" },
    (structure) => Number(structure.price)
  ).with(
    { pricingType: "LINEAR" },
    (structure) => Number(structure.pricePerUnit)
  ).with(
    { pricingType: "PACKAGE" },
    (structure) => Number(structure.pricePerPackage)
  ).with(
    { pricingType: "SEAT_BASED" },
    (structure) => Number(structure.pricePerSeat)
  ).with({ pricingType: "GRADUATED" }, (structure) => {
    const firstTier = structure.tiers.at(0);
    if (!firstTier || !firstTier.fee) {
      return 0;
    }
    return Number(firstTier.fee);
  }).with({ pricingType: "VOLUME" }, () => {
    throw new Error(
      `${priceStructure.pricingType} pricing type is not supported`
    );
  }).exhaustive();
};
var applyDiscountToAmount = ({
  amount,
  discount
}) => {
  if (discount.discountCalculationType === "NOMINAL") {
    return Math.max(0, amount - Number(discount.amount));
  }
  if (discount.discountCalculationType === "PERCENTAGE") {
    return amount * ((100 - Number(discount.amount)) / 100);
  }
  return amount;
};
var getPriceStructureDiscountedAmount = ({
  price,
  discount
}) => {
  const priceStructure = price.structure;
  const isPricingTypeSupported = ["FIXED", "ONE_TIME", "PACKAGE", "SEAT_BASED"].includes(
    priceStructure.pricingType
  ) || priceStructure.pricingType === "LINEAR" && !priceStructure.isPricePercentage;
  if (!isPricingTypeSupported) {
    throw new Error(
      `${priceStructure.pricingType === "LINEAR" ? "LINEAR with price percentage" : priceStructure.pricingType} pricing type not supported`
    );
  }
  const amount = getPriceStructureAmount({ structure: priceStructure });
  return applyDiscountToAmount({ amount, discount });
};
var calculateDiscountedPrice = (props) => {
  const isIncompatiblePricingType = ["VOLUME", "GRADUATED", "LINEAR"].includes(
    props.price.structure.pricingType
  ) || props.price.structure.pricingType === "LINEAR" && props.price.structure.isPricePercentage;
  if (!props.discount || isIncompatiblePricingType) {
    return null;
  }
  const amount = getPriceStructureDiscountedAmount({
    price: props.price,
    discount: props.discount
  });
  return toMoney2({
    currency: props.price.currency,
    value: String(amount)
  });
};

// src/prices/enforceMinimumPrecision.ts
var enforceMinimumPrecision = (precision) => (value) => {
  if (!value) {
    return 0 .toFixed(precision);
  }
  const parsedValue = typeof value === "number" ? value.toString() : value;
  const valueAsNumber = parseFloat(parsedValue);
  if (isNaN(valueAsNumber)) {
    return 0 .toFixed(precision);
  }
  if ((parsedValue.split(".")[1]?.length ?? 0) < precision) {
    return parseFloat(parsedValue).toFixed(precision);
  }
  return parsedValue;
};

// src/prices/priceModel.adapter.ts
import { match as match4 } from "ts-pattern";
import omit from "lodash/fp/omit";
var externalIdsAndParametersAdapter = {
  in: (price) => ({
    externalIds: price.integrationIds?.map(({ service, id }) => ({
      key: service,
      value: id
    })) ?? [],
    parameters: price.customMetricParameters ?? []
  }),
  out: (price) => ({
    integrationIds: price.externalIds?.map(({ key, value }) => ({
      service: key,
      id: value
    })) ?? [],
    customMetricParameters: price.parameters ?? []
  })
};
var priceModelAdapter = {
  in: (price) => {
    return match4(price).with({ structure: { pricingType: "SEAT_BASED" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.in(matchedPrice),
      structure: {
        pricingType: matchedPrice.structure.pricingType,
        pricePerSeat: matchedPrice.structure.pricePerSeat,
        seatTypeId: matchedPrice.structure.seatMetricId,
        includedSeats: matchedPrice.structure.contractedMinimumSeats,
        prorationStrategy: matchedPrice.structure.prorationStrategy,
        tiers: matchedPrice.structure.tiers?.map((t) => ({
          ...t,
          isPricePercentage: false
        })) ?? [],
        chargeMethod: "CHARGE_CONSUMED",
        overagesBillingFrequency: matchedPrice.structure.overagesBillingFrequency,
        prorateFlatFees: false
      }
    })).with({ structure: { pricingType: "FIXED" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.in(matchedPrice)
    })).with({ structure: { pricingType: "ONE_TIME" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.in(matchedPrice)
    })).with({ structure: { pricingType: "GRADUATED" } }, (matchedPrice) => ({
      ...matchedPrice,
      structure: omit("usageCalculationPeriod")(
        matchedPrice.structure
      ),
      ...externalIdsAndParametersAdapter.in(matchedPrice)
    })).with({ structure: { pricingType: "VOLUME" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.in(matchedPrice)
    })).with({ structure: { pricingType: "PACKAGE" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.in(matchedPrice)
    })).with({ structure: { pricingType: "LINEAR" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.in(matchedPrice)
    })).exhaustive();
  },
  out: (price) => {
    return match4(price).with({ structure: { pricingType: "SEAT_BASED" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.out(matchedPrice),
      structure: {
        pricingType: matchedPrice.structure.pricingType,
        pricePerSeat: matchedPrice.structure.pricePerSeat,
        seatMetricId: matchedPrice.structure.seatTypeId,
        contractedMinimumSeats: matchedPrice.structure.includedSeats,
        prorationStrategy: matchedPrice.structure.prorationStrategy,
        tiers: matchedPrice.structure.tiers ?? [],
        overagesBillingFrequency: matchedPrice.structure.overagesBillingFrequency,
        prorateFlatFees: false
      }
    })).with({ structure: { pricingType: "FIXED" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.out(matchedPrice)
    })).with({ structure: { pricingType: "ONE_TIME" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.out(matchedPrice)
    })).with({ structure: { pricingType: "GRADUATED" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.out(matchedPrice),
      structure: {
        ...matchedPrice.structure,
        usageCalculationMode: matchedPrice.structure.usageCalculationMode
      }
    })).with({ structure: { pricingType: "VOLUME" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.out(matchedPrice)
    })).with({ structure: { pricingType: "PACKAGE" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.out(matchedPrice)
    })).with({ structure: { pricingType: "LINEAR" } }, (matchedPrice) => ({
      ...matchedPrice,
      ...externalIdsAndParametersAdapter.out(matchedPrice)
    })).exhaustive();
  }
};

// src/prices/toPriceSummary.ts
var toPriceSummary = (price) => {
  const adaptedPrice = priceModelAdapter.in(price);
  switch (adaptedPrice.structure.pricingType) {
    case "FIXED":
      return toMoney(
        {
          currency: adaptedPrice.currency,
          value: adaptedPrice.structure.price
        },
        "",
        10
      );
    case "ONE_TIME":
      return toMoney(
        {
          currency: adaptedPrice.currency,
          value: adaptedPrice.structure.price
        },
        "",
        10
      );
    case "LINEAR":
      if (adaptedPrice.structure.isPricePercentage) {
        return `${adaptedPrice.structure.pricePerUnit}% (${adaptedPrice.currency})`;
      }
      return toMoney(
        {
          currency: adaptedPrice.currency,
          value: adaptedPrice.structure.pricePerUnit
        },
        "",
        10
      );
    case "PACKAGE":
      return `${toMoney(
        {
          currency: adaptedPrice.currency,
          value: adaptedPrice.structure.pricePerPackage
        },
        "",
        10
      )} per package`;
    case "VOLUME":
    case "GRADUATED":
      if (adaptedPrice.structure.tiers[0].isPricePercentage) {
        return `Starts from ${adaptedPrice.structure.tiers[0].price}% (${adaptedPrice.currency})`;
      } else {
        return `Starts from ${toMoney(
          {
            currency: adaptedPrice.currency,
            value: adaptedPrice.structure.tiers[0].price
          },
          "",
          10
        )}${adaptedPrice.structure.tiers[0].fee && Number.parseFloat(adaptedPrice.structure.tiers[0].fee) > 0 ? ` + ${toMoney(
          {
            currency: adaptedPrice.currency,
            value: adaptedPrice.structure.tiers[0].fee
          },
          "",
          10
        )}` : ""}`;
      }
    case "SEAT_BASED":
      if (adaptedPrice.structure.tiers?.length) {
        return `Starts from ${toMoney(
          {
            currency: adaptedPrice.currency,
            value: adaptedPrice.structure.tiers[0].price
          },
          "",
          10
        )}`;
      }
      return `${toMoney(
        {
          currency: adaptedPrice.currency,
          value: adaptedPrice.structure.pricePerSeat
        },
        "",
        10
      )} per seat`;
  }
};

// src/prices/deriveIsPercentage.ts
var deriveIsPercentage = (structure) => {
  if (!structure) {
    return false;
  }
  if (structure.pricingType === "VOLUME") {
    return structure.tiers[0].isPricePercentage;
  }
  if (structure.pricingType === "GRADUATED") {
    return structure.tiers[0].isPricePercentage;
  }
  if (structure.pricingType === "LINEAR") {
    return structure.isPricePercentage;
  }
  return false;
};

// src/prices/priceStructureToUrlChunk.ts
var priceStructureToUrlChunk = (structure) => {
  switch (structure.pricingType) {
    case "FIXED":
      return "standard";
    case "ONE_TIME":
      return "standard";
    case "LINEAR":
      return "linear";
    case "PACKAGE":
      return "packaged";
    case "VOLUME":
      return `volume${deriveIsPercentage(structure) ? "-percentage" : ""}`;
    case "GRADUATED":
      return `graduated${deriveIsPercentage(structure) ? "-percentage" : ""}`;
    case "SEAT_BASED":
      return "seat";
  }
};

// src/prices/toDiscountAmount.ts
var toDiscountAmount = ({
  discountCalculationType,
  discountAmount,
  currency
}) => {
  if (discountCalculationType === "PERCENTAGE") {
    return `${discountAmount}% off`;
  } else {
    const monetaryValue = toMoney(
      {
        currency,
        value: discountAmount.toString()
      },
      "",
      4
    );
    return `${monetaryValue} off`;
  }
};

// src/assets/assets.constants.ts
var SEQUENCE_ASSET_PATH_ROOT = "seq-asset://";
var SEQUENCE_ASSET_DOMAINS = [
  "https://assets.dev.seqhq.io",
  "https://assets.sandbox.sequencehq.com",
  "https://assets.sequencehq.com",
  SEQUENCE_ASSET_PATH_ROOT
];

// src/assets/constructAssetPath.ts
var constructAssetPath = ({
  accountId,
  assetId
}) => {
  return `${SEQUENCE_ASSET_PATH_ROOT}${accountId}/${assetId}`;
};

// src/assets/isExternalAssetUrl.ts
var isExternalAssetUrl = (url) => !SEQUENCE_ASSET_DOMAINS.some((domain) => url.startsWith(domain));

// src/assets/parseInternalAssetUrl.ts
var parseInternalAssetUrl = (url) => {
  if (isExternalAssetUrl(url)) {
    return void 0;
  }
  const pattern = /\/([0-9a-f-]+)(?:\/.*)?\/([0-9a-f-]+)/;
  const match5 = url.match(pattern);
  if (!match5) {
    return void 0;
  }
  const accountId = match5.at(1);
  const assetId = match5.at(2);
  if (!accountId || !assetId) {
    return void 0;
  }
  return { accountId, assetId };
};

// src/assets/useGetSignedAssetUrl.tsx
import { useEffect as useEffect2, useState as useState2 } from "react";

// src/assets/getSignedAssetUrl.ts
var getSignedAssetUrl = async ({
  url,
  assetLoader
}) => {
  if (url === "") {
    return void 0;
  }
  const isExternalAsset = isExternalAssetUrl(url);
  if (isExternalAsset) {
    return url;
  }
  const parsedInternalAssetUrl = parseInternalAssetUrl(url);
  if (!parsedInternalAssetUrl) {
    return void 0;
  }
  const asset = await assetLoader(parsedInternalAssetUrl);
  if (!asset) {
    return void 0;
  }
  return asset.urlSigned;
};

// src/assets/useGetSignedAssetUrl.tsx
var useGetSignedAssetUrl = ({
  url,
  assetLoader
}) => {
  const [signedUrl, setSignedUrl] = useState2();
  useEffect2(() => {
    const getAssetUrl = async () => {
      const signedUrlRes = await getSignedAssetUrl({ url, assetLoader });
      setSignedUrl(signedUrlRes);
    };
    void getAssetUrl();
  }, [url]);
  return signedUrl;
};

// src/pdfs/registerReactPdfFonts.ts
import { Font } from "@react-pdf/renderer";
var registerReactPdfFonts = () => {
  const BASE_URL = "https://dashboard.sequencehq.com";
  Font.register({
    family: "Lato",
    fonts: [
      {
        src: `${BASE_URL}/fonts/Lato-Regular.ttf`,
        fontStyle: "normal",
        fontWeight: "normal"
      },
      {
        src: `${BASE_URL}/fonts/Lato-Bold.ttf`,
        fontStyle: "normal",
        fontWeight: "bold"
      },
      {
        src: `${BASE_URL}/fonts/Lato-Italic.ttf`,
        fontStyle: "italic",
        fontWeight: "normal"
      },
      {
        src: `${BASE_URL}/fonts/Lato-BoldItalic.ttf`,
        fontStyle: "italic",
        fontWeight: "bold"
      }
    ]
  });
};

// src/billingSchedule/toBillingScheduleStatusBadgeProps.ts
var statusToFormat3 = {
  DRAFT: {
    children: "Draft",
    sentiment: "neutral"
  },
  PENDING: {
    children: "Upcoming",
    sentiment: "highlight"
  },
  ACTIVE: {
    children: "Active",
    sentiment: "info"
  },
  SUSPENDED: {
    children: "Suspended",
    sentiment: "warning"
  },
  CANCELLED: {
    children: "Cancelled",
    sentiment: "error"
  },
  COMPLETED: {
    children: "Completed",
    sentiment: "success"
  }
};
var toBillingScheduleStatusBadgeProps = ({
  status,
  size = "md"
}) => {
  return {
    ...statusToFormat3[status],
    size
  };
};
export {
  arrayToIdKeyedObject,
  calculateDiscountedPrice,
  constructAssetPath,
  convertBlobToBase64,
  decimalFromPercentage,
  developerLog,
  downloadBlob,
  enforceMinimumPrecision,
  evaluateValidators,
  formatAddress,
  getAuthConfig,
  getAuthWebConfig,
  getNodeSentryConfig,
  getPortalDomain,
  getReactSentryConfig,
  getSdkConfig,
  getSignedAssetUrl,
  getUserInfoConfig,
  isExternalAssetUrl,
  isExternalLink,
  parseInternalAssetUrl,
  percentageFromDecimal,
  priceModelAdapter,
  priceStructureToUrlChunk,
  registerReactPdfFonts,
  toBillingFrequencyLabel,
  toBillingFrequencyName,
  toBillingFrequencyPluralName,
  toBillingScheduleStatusBadgeProps,
  toBillingType,
  toDiscountAmount,
  toInvoicePaymentStatusBadgeProps,
  toInvoiceStatusBadgeProps,
  toMoney,
  toPriceSummary,
  toStateLabel,
  useForm,
  useGetSignedAssetUrl
};
//# sourceMappingURL=index.js.map